/**
 * SendQuoteDialog — Modal d'envoi de devis par email.
 *
 * Ouvert depuis SalesDocumentEditor en mode devis (bouton "Envoyer").
 * Pré-remplit destinataire = client.email, éditable + CC libre.
 * POST /api/quotes/{id}/send génère le PDF côté backend, envoie via Resend,
 * et bascule le devis en `sent`.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Send, Paperclip } from "lucide-react";
import { toast } from "sonner";

export default function SendQuoteDialog({ quote, open, onOpenChange, onSent }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !quote) return;
    setTo(quote.client?.email || "");
    setCc("");
    setSubject(`Devis ${quote.number || ""}`.trim());
    const total = new Intl.NumberFormat("fr-FR", {
      style: "currency", currency: "EUR", minimumFractionDigits: 2,
    }).format(quote.totals?.total_ttc ?? quote.total_ttc ?? 0);
    setMessage(
      `Bonjour${quote.client?.contact_name ? " " + quote.client.contact_name : ""},\n\n` +
      `Vous trouverez en pièce jointe le devis ${quote.number || ""} ` +
      `d'un montant de ${total} TTC.\n\n` +
      `N'hésitez pas à revenir vers nous pour toute question ou ajustement.\n\n` +
      `Bien cordialement.`
    );
  }, [open, quote]);

  const send = async () => {
    if (!to.trim()) { toast.error("Adresse email destinataire requise."); return; }
    if (!quote?.id) return;
    setSending(true);
    try {
      const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post(`/quotes/${quote.id}/send`, {
        to_email: to.trim(),
        cc: ccList.length ? ccList : undefined,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      toast.success(`Devis envoyé à ${data.sent_to}`);
      onSent?.(data);
      onOpenChange?.(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-quote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"
                        style={{ fontFamily: "Manrope, sans-serif" }}>
            <Send className="w-5 h-5 text-blue-600" />
            Envoyer le devis {quote?.number}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Le PDF est généré et joint automatiquement. Le devis passera en statut « Envoyé ».
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500">Destinataire *</Label>
            <Input type="email" value={to} onChange={(e) => setTo(e.target.value)}
                    className="mt-1 bg-white border-slate-200"
                    placeholder="client@exemple.fr"
                    data-testid="send-quote-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Copie (CC) — séparés par des virgules</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)}
                    className="mt-1 bg-white border-slate-200"
                    placeholder="daf@exemple.fr, direction@exemple.fr"
                    data-testid="send-quote-cc" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Objet</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                    className="mt-1 bg-white border-slate-200"
                    data-testid="send-quote-subject" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                       className="mt-1 bg-white border-slate-200 min-h-[140px] text-sm"
                       data-testid="send-quote-message" />
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2">
            <Paperclip className="h-3.5 w-3.5" />
            <span>Devis-{quote?.number || "brouillon"}.pdf joint automatiquement</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)}>Annuler</Button>
          <Button onClick={send} disabled={sending}
                   className="bg-blue-600 hover:bg-blue-700 text-white"
                   data-testid="send-quote-confirm-btn">
            <Send className="w-4 h-4 mr-1.5" />
            {sending ? "Envoi…" : "Envoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
